import {ChangeDetectionStrategy, Component, computed, input} from '@angular/core';
import {Router} from '@angular/router';
import * as dayjs from 'dayjs';
import {CustomerFeatureTypeEnum} from 'src/app/enums/AdEnums';
import {BookingTypeEnum} from 'src/app/enums/BookingEnums';
import {EventActionEnum, EventCategoryEnum} from 'src/app/enums/GaTrackingEnums';
import {ToolsService} from 'src/app/services/tools.service';
import {UrlBuilderService} from 'src/app/services/url-builder.service';
import {CareproviderMapItemTranslated} from 'src/app/types/CareproviderMapItemTranslated';
import {CareService} from 'src/app/types/CareService';
import {MapData} from 'src/app/types/MapData';
import {OpenHour} from 'src/app/types/OpenHour';
import {SearchResultsCareproviderUnitItem} from 'src/app/types/SearchResultsCareproviderUnitItem';
import {SearchState} from 'src/app/types/SearchState';
import {SearchTimeslot} from 'src/app/types/SearchTimeslot';
import urlJoin from 'url-join';
import {DLPageTypeEnum} from '../../../../../../enums/DataLayerEnums';
import {GtmEventEnum} from '../../../../../../enums/GoogleEnums';
import {ModalTypeEnum} from '../../../../../../enums/ModalEnums';
import {AppConfigService} from '../../../../../../services/app-config.service';
import {CareproviderUnitService} from '../../../../../../services/careprovider-unit.service';
import {GtmGa4EventPushService} from '../../../../../../services/gtm-ga4-event-push.service';
import {GtmService} from '../../../../../../services/gtm.service';
import {ModalsControlService} from '../../../../../../services/modals-control.service';
import {BookingConfigurationData} from '../../../../../../types/BookingConfigurationData';
import {DataLayerParamsClickGA4} from '../../../../../../types/DataLayerParamsClickGA4';
import {ModalCommand} from '../../../../../../types/ModalCommand';
import {TimeslotDetails} from '../../../../../../types/TimeslotDetails';

interface VisibleCareService extends CareService {
  bookingConfigurationDataSection1: BookingConfigurationData[];
  bookingConfigurationDataSection2: BookingConfigurationData[];
}

@Component({
  selector: 'app-careprovider-unit-item-service',
  templateUrl: './careprovider-unit-item-service.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CareproviderUnitItemServiceComponent {
  careproviderUnit = input<SearchResultsCareproviderUnitItem>();
  itemIndex = input<number>();
  searchState = input<SearchState>();
  pageType = input<DLPageTypeEnum>();
  careService = computed<VisibleCareService>(() => {
    if (
      this.careproviderUnit() &&
      this.careproviderUnit().careServices &&
      this.careproviderUnit().careServices.length > 0
    ) {
      return this.prepareVisibleCareService(this.careproviderUnit().careServices[0]);
    }
    return null;
  });
  careproviderUrl = computed<string>(() =>
    this.urlBuilder.CreateStaticLink(
      this.urlBuilder.CreateCareproviderUnitRouterLink(this.careproviderUnit()?.id, this.careproviderUnit()?.urlPath),
    ),
  );

  hasImage = computed(
    () => !this.tools.isNil(this.careproviderUnit().thumbnail) && this.careproviderUnit().thumbnail !== '',
  );
  imageUrl = computed(() =>
    this.hasImage() ? `${this.appConfigService.appConfig.amazonS3Url}${this.careproviderUnit().thumbnail}` : null,
  );
  logoUrl = computed<string>(() => {
    if (!this.careproviderUnit() || !this.careproviderUnit().logoUrl) {
      return null;
    }
    return this.tools.getImageUrl(this.careproviderUnit().logoUrl);
  });
  isPriorityImage = computed(() => this.itemIndex() === 0);
  featureLabel = computed(() => `${this.careproviderUnit().name}-${this.careproviderUnit().id}`);

  mainSegment = computed(() => this.careproviderUnit()?.mainCaregiverUnitType?.segment?.name?.toLowerCase());
  address = computed(() => {
    const name = this.careproviderUnit().place?.name;
    return name ? name : this.careproviderUnit().address?.latitude ? 'Se på kartan' : null;
  });
  customGa4Params = computed<DataLayerParamsClickGA4>(() => this.buildDataLayerParamsClickGA4(this.careproviderUnit()));
  public isInternal = computed(() =>
    this.careProviderUnitService.isInternalCareService(this.careService(), this.careproviderUnit().bookingSettings),
  );

  public eventCategoryEnum = EventCategoryEnum;
  public eventActionEnum = EventActionEnum;
  public bookingTypeEnum = BookingTypeEnum;
  public showNextMonthTimeslots = false;

  constructor(
    private urlBuilder: UrlBuilderService,
    private tools: ToolsService,
    private router: Router,
    private modalControl: ModalsControlService,
    private gtmService: GtmService,
    private gtmEventPush: GtmGa4EventPushService,
    private appConfigService: AppConfigService,
    private careProviderUnitService: CareproviderUnitService,
  ) {
    dayjs.locale('sv');
    this.showNextMonthTimeslots = this.appConfigService.appConfig.features.showNextMonthTimeslots === true;
  }

  public initTimeslots(careService: CareService): Array<TimeslotDetails> {
    if (this.tools.isNil(careService.timeSlots) || careService.timeSlots.length === 0) {
      return [];
    }
    return careService.timeSlots.map((ts: string) => this.genTimeslotDetails(careService.id, ts));
  }

  public openMap(): void {
    const markers = this.getMarker();
    this.showMap(markers);
  }

  public generateDataLayerParamsClickGA4(
    callToAction: string,
    careService: CareService,
    index: number,
  ): DataLayerParamsClickGA4 {
    const params: DataLayerParamsClickGA4 = this.tools.clone(this.customGa4Params());
    if (careService) {
      params.careServiceId = careService.id;
      params.careServiceTypeId = careService.careServiceTypeId;
    }
    params.callToAction = callToAction;
    if (
      careService &&
      !this.tools.isNil(careService.bookingConfigurationData) &&
      !this.tools.isNil(
        careService.bookingConfigurationData[index] &&
          !this.tools.isNil(careService.bookingConfigurationData[index].bookingSettings),
      )
    ) {
      params.bookingType = careService.bookingConfigurationData[index].bookingSettings.bookingType.name;
    }
    return this.tools.clone(params);
  }

  private genTimeslotDetails(careServiceId: number, timeslot: string): TimeslotDetails {
    const day = dayjs(timeslot);
    let label = day.format('ddd D/M');
    label = label[0].toUpperCase() + label.slice(1);
    const timeslotRouterParam = day.unix().toString();

    if (
      this.searchState().timeslot &&
      this.searchState().timeslot.indexOf(SearchTimeslot.TODAY) >= 0 &&
      day.date() === dayjs().date()
    ) {
      label = 'Idag';
    }

    if (
      this.searchState().timeslot &&
      this.searchState().timeslot.indexOf(SearchTimeslot.TOMORROW) >= 0 &&
      day.date() === dayjs().add(1, 'day').date()
    ) {
      label = 'Imorgon';
    }

    const referral: string = urlJoin(this.appConfigService.appConfig.baseUrl, this.router.url);
    return {
      label: `${label} ${day.format('HH.mm')}`,
      routerLink: this.urlBuilder.CreateInternalBookingRouterLink(
        this.careproviderUnit().id,
        careServiceId,
        timeslotRouterParam,
      ),
      queryParams: this.urlBuilder.CreateInternalBookingQueryParams(referral),
      labelDay: `${label}`,
      labelTime: `${day.format('HH.mm')}`,
    };
  }

  private areCareServicesVisible(): boolean {
    return (
      this.careproviderUnit() &&
      this.careproviderUnit().isCustomer === true &&
      !this.tools.isNil(this.careproviderUnit().careServices) &&
      this.careproviderUnit().careServices.length > 0
    );
  }

  private updateUrlForCareService(careService: CareService): CareService {
    if (this.areCareServicesVisible()) {
      careService.url = this.urlBuilder.CreateStaticLink(
        this.urlBuilder.CreateCareServiceRouterLink(
          this.careproviderUnit().id,
          this.careproviderUnit().urlPath,
          careService.id,
          careService.urlPath,
        ),
      );
    }
    return careService;
  }

  private updateBookingConfiguration(careService: CareService): CareService {
    const telephoneHoursFiltered: OpenHour[] =
      this.careproviderUnit().telephoneHours != null
        ? this.careproviderUnit().telephoneHours
        : this.careproviderUnit().openHours != null
          ? this.careproviderUnit().openHours
          : [];

    careService.bookingConfigurationData = [];
    careService.bookingConfigurationData.push({
      id: this.careproviderUnit().id,
      careserviceId: careService.id,
      careserviceName: careService.name,
      careserviceTypeId: careService.careServiceTypeId,
      availableTimeSlots: careService.timeSlots,
      mainCaregiverUnitTypeId: this.careproviderUnit().mainCaregiverUnitType?.id,
      areaId: this.careproviderUnit().mainCaregiverUnitType?.area?.id,
      name: this.careproviderUnit().name,
      placeId:
        this.careproviderUnit().places && this.careproviderUnit().places.length > 0
          ? this.careproviderUnit().places[0].id
          : 0,
      telephoneHours: telephoneHoursFiltered,
      bookingSettings: careService.careServiceBookingSettings
        ? careService.careServiceBookingSettings
        : this.careproviderUnit() && this.careproviderUnit().bookingSettings[0]
          ? this.careproviderUnit().bookingSettings[0]
          : null,
      customerFeature: this.careproviderUnit().customerFeatures.find(
        (item) => item.type === CustomerFeatureTypeEnum.MODAL_BOOKING,
      ),
      allCustomerFeatures: this.careproviderUnit().customerFeatures,
      hsaName:
        this.careproviderUnit().hsaObjects && this.careproviderUnit().hsaObjects.length > 0
          ? this.careproviderUnit().hsaObjects[0].publicName
          : this.careproviderUnit().name,
    });

    return careService;
  }

  private prepareVisibleCareService(careService: CareService): VisibleCareService {
    careService.timeslotDetails = this.initTimeslots(careService);
    careService = this.updateUrlForCareService(careService);
    careService = this.updateBookingConfiguration(careService);

    const bookingConfigurationDataSection1: BookingConfigurationData[] = [];
    const bookingConfigurationDataSection2: BookingConfigurationData[] = [];
    if (careService.bookingConfigurationData) {
      careService.bookingConfigurationData.forEach((item, index) => {
        if (
          item?.bookingSettings?.bookingType?.name !== BookingTypeEnum.VARDEN &&
          item?.bookingSettings?.bookingType?.name !== BookingTypeEnum.INTERNAL &&
          item?.bookingSettings?.bookingType?.name !== BookingTypeEnum.ERRANDSYSTEM
        ) {
          bookingConfigurationDataSection2.push(item);
        }
        if (
          item?.bookingSettings?.bookingType?.name === BookingTypeEnum.VARDEN ||
          item?.bookingSettings?.bookingType?.name === BookingTypeEnum.INTERNAL ||
          item?.bookingSettings?.bookingType?.name === BookingTypeEnum.ERRANDSYSTEM
        ) {
          bookingConfigurationDataSection2.push(item);
        }
      });
    }
    return {
      ...careService,
      bookingConfigurationDataSection1,
      bookingConfigurationDataSection2,
    };
  }

  private showMap(markers: Array<CareproviderMapItemTranslated>) {
    if (markers.length > 0) {
      const modalCommand: ModalCommand<MapData> = {
        type: ModalTypeEnum.GOOGLE_MAP,
        data: {height: 80, markers},
      };
      this.modalControl.openModal(modalCommand);
      this.pushGtmTag('show map');
    } else {
      this.pushGtmTag('map not shown');
    }
  }

  private pushGtmTag(message: string) {
    const gtmTag = {
      event: GtmEventEnum.DEFAULT,
      Category: 'Search result',
      Action: message,
      Label: '',
      Value: 1,
    };

    this.gtmService.pushTag(gtmTag);

    const customGa4Params: DataLayerParamsClickGA4 = {
      callToAction: `Search result - ${message}`,
      cpruId: this.careproviderUnit().id,
      pageType: this.pageType(),
    };
    const gtmGa4Tag = {
      event: GtmEventEnum.CTA_CLICK,
      ...customGa4Params,
    };
    this.gtmEventPush.pushClickEvent(gtmGa4Tag);
  }

  private getMarker(): Array<CareproviderMapItemTranslated> {
    const markers: Array<CareproviderMapItemTranslated> = [];

    if (
      this.careproviderUnit() &&
      this.careproviderUnit().address &&
      this.careproviderUnit().address.latitude &&
      this.careproviderUnit().address.latitude !== 0
    ) {
      if (this.careproviderUnit().address.street === null) {
        this.careproviderUnit().address.street = '';
      }
      if (this.careproviderUnit().address.city === null) {
        this.careproviderUnit().address.city = '';
      }
      markers.push({
        id: this.careproviderUnit().id,
        name: this.careproviderUnit().name,
        urlPath: this.careproviderUnit().urlPath,
        location: {
          lat: this.careproviderUnit().address.latitude,
          lng: this.careproviderUnit().address.longitude,
        },
        rating: this.careproviderUnit().rating,
        address: this.careproviderUnit().address,
        thumbnail: this.careproviderUnit().thumbnail,
      });
    }

    return markers;
  }

  private buildDataLayerParamsClickGA4(careproviderUnit: SearchResultsCareproviderUnitItem): DataLayerParamsClickGA4 {
    const customGa4Params: DataLayerParamsClickGA4 = {
      callToAction: '',
      cpruId: careproviderUnit.id,
      inferredCareProviderTypeAreaId: careproviderUnit.mainCaregiverUnitType?.area?.id,
      mainCareProviderTypeId: careproviderUnit.mainCaregiverUnitType?.id,
      inferredCategoryTypeId: careproviderUnit.mainCaregiverUnitType?.id,
      pageType: this.pageType(),
    };
    if (careproviderUnit.bookingSettings) {
      careproviderUnit.bookingSettings.forEach((bookingSetting) => {
        if (!this.tools.isNil(bookingSetting) && !this.tools.isNil(bookingSetting.bookingType)) {
          customGa4Params.bookingType = bookingSetting.bookingType.name;
        }
      });
    }
    return customGa4Params;
  }
}
